/**
 * Supabase Client Configuration
 * 
 * SECURITY NOTES:
 * - Only uses ANON KEY (publishable, safe for frontend)
 * - NEVER expose SERVICE_ROLE_KEY in frontend
 * - All data operations should be protected by RLS policies
 * - Sensitive operations must go through secure backend
 */
import { useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import getEnvironmentConfig from './config/environment'


//import { supabase } from "../supabaseClient";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;


// SAFETY CHECK
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Missing Supabase env variables");
}

// CREATE CLIENT
export const supabase = createClient(supabaseUrl, supabaseAnonKey);

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Environment configuration error");
}

//export const supabase = createClient(supabaseUrl, supabaseAnonKey);
const config = getEnvironmentConfig()

if (!config) {
  throw new Error(
    'Supabase configuration failed. Check environment variables in .env.local'
  )
}
/*
export const supabase = createClient(config.supabaseUrl, config.supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Security: Require explicit sign-out
    storage: window.localStorage,
    storageKey: 'motohubjd-auth', // Namespaced key to prevent conflicts
  },
  // Global headers for all requests
  headers: {
    'X-Client-Info': 'motohub-web/1.0.0',
  }
})
*/
// Helper functions for authentication
export const auth = {
  // Sign up with email/password
  signUpWithEmail: async (email, password, profile = {}) => {
    try {
      // Validate inputs
      if (!email || !password) {
        return { data: null, error: new Error('Email and password are required') }
      }
      if (password.length < 6) {
        return { data: null, error: new Error('Password must be at least 6 characters') }
      }

      const emailRedirectTo =
        typeof window !== 'undefined' ? `${window.location.origin}/login` : undefined

      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          emailRedirectTo,
          data: {
            name: (profile.name || '').trim().substring(0, 100),
            city: (profile.city || '').trim().substring(0, 50),
            bike: (profile.bike || '').trim().substring(0, 100),
            role: (profile.role || 'rider').trim().toLowerCase().substring(0, 20),
          },
        },
      })

      if (error) {
        console.error('[Auth] Sign up error:', error.message)
        return { data: null, error }
      }

      return { data, error: null }
    } catch (err) {
      console.error('[Auth] Unexpected error during sign up:', err)
      return { data: null, error: err }
    }
  },

  // Sign in with email/password
  signInWithEmail: async (email, password) => {
    try {
      if (!email || !password) {
        return { data: null, error: new Error('Email and password are required') }
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      })

      if (error) {
        console.error('[Auth] Sign in error:', error.message)
        return { data: null, error }
      }

      return { data, error: null }
    } catch (err) {
      console.error('[Auth] Unexpected error during sign in:', err)
      return { data: null, error: err }
    }
  },

  // Sign out
  signOut: async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('[Auth] Sign out error:', error.message)
      }
      return { error }
    } catch (err) {
      console.error('[Auth] Unexpected error during sign out:', err)
      return { error: err }
    }
  },

  // Get current user (safe - doesn't expose sensitive data)
  getCurrentUser: async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      return { user, error }
    } catch (err) {
      console.error('[Auth] Error getting current user:', err)
      return { user: null, error: err }
    }
  },

  // Get user session
  getSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      return { session, error }
    } catch (err) {
      console.error('[Auth] Error getting session:', err)
      return { session: null, error: err }
    }
  }
}

// Helper functions for user profiles
export const userProfiles = {
  // Get user profile
  getProfile: async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, name, email, phone, bio, city, state, instagram, youtube, riding_style, is_public, bikes_owned, avatar_url, bike, experience, preferred_ride, verified, role')
      .eq('id', userId)
      .single()
    return { data, error }
  },

  // Update user profile
  updateProfile: async (userId, updates) => {
    const { data, error } = await supabase
      .from('profiles')
      .update({ ...updates })
      .eq('id', userId)
      .select()
      .single()
    return { data, error }
  },

  // Create user profile
  createProfile: async (profile) => {
    const { data, error } = await supabase
      .from('profiles')
      .insert([profile])
      .select()
      .single()
    return { data, error }
  },

  // Create or update user profile
  upsertProfile: async (profile) => {
    const { data, error } = await supabase
      .from('profiles')
      .upsert([profile], { onConflict: 'id' })
      .select()
      .single()
    return { data, error }
  }
}

// Helper functions for rides
export const rides = {
  // Get all rides
  getAllRides: async () => {
    try {
      const { data, error } = await supabase
        .from('rides')
        .select('*')
        .order('date', { ascending: false })
      
      if (error) {
        console.error('getAllRides error:', error);
        return { data: [], error };
      }
      
      return { data: data || [], error: null };
    } catch (err) {
      console.error('getAllRides exception:', err);
      return { data: [], error: err };
    }
  },

  // Get ride by ID
  getRideById: async (rideId) => {
    const { data, error } = await supabase
      .from('rides')
      .select(`
        *,
        organizer:users(name, avatar_url, city),
        participants:ride_participants(
          user:users(name, avatar_url, bike)
        )
      `)
      .eq('id', rideId)
      .single()
    return { data, error }
  },

  // Create new ride
  createRide: async (rideData) => {
    const { data, error } = await supabase
      .from('rides')
      .insert([rideData])
      .select()
      .single()
    return { data, error }
  },

  // Join ride
  joinRide: async (rideId, userId) => {
    const { data, error } = await supabase
      .from('ride_participants')
      .insert([{ ride_id: rideId, user_id: userId }])
    return { data, error }
  },

  // Leave ride
  leaveRide: async (rideId, userId) => {
    const { error } = await supabase
      .from('ride_participants')
      .delete()
      .eq('ride_id', rideId)
      .eq('user_id', userId)
    return { error }
  },

  // Update ride
  updateRide: async (rideId, rideData) => {
    const { error } = await supabase
      .from('rides')
      .update(rideData)
      .eq('id', rideId)
    return { data: null, error }
  },

  // Delete ride
  deleteRide: async (rideId) => {
    const { error } = await supabase
      .from('rides')
      .delete()
      .eq('id', rideId)
    return { error }
  }
}
